// NDLCS Academy: course enrolment, quizzes, final exam, admin sign-off.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

const CONTRACT_VERSION = 1;

// ---------- Public ----------

export const listAcademyCourses = createServerFn({ method: "GET" }).handler(async () => {
  const { createClient } = await import("@supabase/supabase-js");
  const sb = createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false, autoRefreshToken: false } },
  );
  const { data, error } = await sb
    .from("course_catalog")
    .select("slug, code, title, category, summary, estimated_hours, pass_threshold")
    .eq("is_published", true)
    .order("code");
  if (error) throw new Error(error.message);
  return { courses: data ?? [] };
});

// ---------- Learner ----------

export const enrolInCourse = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ slug: z.string().min(2).max(60) }).parse(i))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const { data: course, error: cErr } = await sb
      .from("course_catalog")
      .select("slug, code, title")
      .eq("slug", data.slug)
      .maybeSingle();
    if (cErr || !course) throw new Error("Course not found");

    // Upsert progress
    const { data: existing } = await sb
      .from("course_progress")
      .select("id, status, contract_accepted_at")
      .eq("user_id", context.userId)
      .eq("course_slug", data.slug)
      .maybeSingle();

    let wasNew = false;
    if (!existing) {
      const { error: pErr } = await sb.from("course_progress").insert({
        user_id: context.userId,
        course_slug: data.slug,
        status: "in_progress",
        contract_accepted_at: new Date().toISOString(),
        contract_version: CONTRACT_VERSION,
      });
      if (pErr) throw new Error(pErr.message);
      wasNew = true;
    }

    // Mirror to legacy free-course enrollments (so dashboard counts still work)
    await sb.from("student_module_enrollments").upsert({
      user_id: context.userId,
      module_code: course.code,
      module_name: course.title,
      course_type: "free",
      status: "enrolled",
    }, { onConflict: "user_id,module_code,course_type" });

    // Send contract email (on first enrol only)
    if (wasNew) {
      try {
        const { data: profile } = await sb
          .from("profiles")
          .select("email, full_name, student_number")
          .eq("id", context.userId)
          .maybeSingle();
        if (profile?.email) {
          const { enqueueTransactionalEmail } = await import("@/lib/email/enqueue.server");
          await enqueueTransactionalEmail({
            templateName: "course-enrolment-contract",
            recipientEmail: profile.email,
            idempotencyKey: `contract-${context.userId}-${data.slug}`,
            label: "course-enrolment-contract",
            templateData: {
              fullName: profile.full_name || "Learner",
              studentNumber: profile.student_number || "—",
              courseTitle: course.title,
              courseCode: course.code,
              enrolledAt: new Date().toLocaleDateString("en-ZA", { year: "numeric", month: "long", day: "numeric" }),
              contractVersion: CONTRACT_VERSION,
            },
          });
        }
      } catch (e) {
        console.error("[enrolInCourse] contract email failed", e);
      }
    }
    return { ok: true, wasNew };
  });

/** Returns course + modules + sanitized questions + the learner's progress. */
export const getCourseForLearner = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ slug: z.string().min(2).max(60) }).parse(i))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: course, error: cErr } = await sb
      .from("course_catalog")
      .select("*")
      .eq("slug", data.slug)
      .maybeSingle();
    if (cErr || !course) throw new Error("Course not found");

    const { data: modules } = await sb
      .from("course_modules")
      .select("id, order_index, title, body_md, lesson_md, summary, key_points, scenario_md, activity_md, notes_url, checklist_url")
      .eq("course_slug", data.slug)
      .order("order_index");

    // Service role: read questions, then strip correct_index before returning
    const { data: rawQ } = await supabaseAdmin
      .from("course_questions")
      .select("id, module_id, kind, order_index, prompt, options, points, explanation")
      .eq("course_slug", data.slug)
      .order("kind")
      .order("module_id")
      .order("order_index");

    const { data: progress } = await sb
      .from("course_progress")
      .select("*")
      .eq("user_id", context.userId)
      .eq("course_slug", data.slug)
      .maybeSingle();

    const { data: attempts } = await sb
      .from("course_attempts")
      .select("id, module_id, kind, score, max_score, passed, attempted_at")
      .eq("user_id", context.userId)
      .eq("course_slug", data.slug)
      .order("attempted_at", { ascending: false });

    return {
      course,
      modules: modules ?? [],
      questions: rawQ ?? [],
      progress: progress ?? null,
      attempts: attempts ?? [],
    };
  });

/** Returns the learner's in-progress / awaiting-review enrolments with the
 * data needed to render a pre-issue certificate preview. */
export const listMyUpcomingCertificates = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const sb = context.supabase;
    const { data: profile } = await sb
      .from("profiles")
      .select("full_name, email")
      .eq("id", context.userId)
      .maybeSingle();

    const { data: rows } = await sb
      .from("course_progress")
      .select("id, course_slug, status, final_score, contract_accepted_at")
      .eq("user_id", context.userId)
      .in("status", ["in_progress", "awaiting_review"]);

    const slugs = (rows ?? []).map((r: any) => r.course_slug);
    const { data: courses } = slugs.length
      ? await sb.from("course_catalog").select("slug, code, title, category").in("slug", slugs)
      : { data: [] as any[] };
    const byslug = new Map((courses ?? []).map((c: any) => [c.slug, c]));

    return {
      recipientName: profile?.full_name || profile?.email || "Learner",
      items: (rows ?? []).map((r: any) => ({
        progressId: r.id,
        courseSlug: r.course_slug,
        status: r.status,
        finalScore: r.final_score,
        course: byslug.get(r.course_slug) ?? null,
      })),
    };
  });



const submitQuizInput = z.object({
  courseSlug: z.string().min(2).max(60),
  moduleId: z.string().uuid(),
  answers: z.array(z.number().int().min(0).max(10)).min(1).max(50).optional(),
  responses: z
    .array(
      z.object({
        questionId: z.string().uuid(),
        selectedIndex: z.number().int().min(0).max(10),
      }),
    )
    .min(1)
    .max(50)
    .optional(),
  elapsedSeconds: z.number().int().min(0).max(60 * 60 * 4).optional(),
});

export const submitModuleQuiz = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => submitQuizInput.parse(i))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: course } = await sb
      .from("course_catalog")
      .select("pass_threshold, retake_cooldown_minutes, max_attempts_per_quiz")
      .eq("slug", data.courseSlug)
      .maybeSingle();
    const threshold = course?.pass_threshold ?? 60;
    const cooldownMin = course?.retake_cooldown_minutes ?? 0;
    const maxAttempts = course?.max_attempts_per_quiz ?? 0;

    // Retake policy enforcement
    const { data: prior } = await sb
      .from("course_attempts")
      .select("id, passed, attempted_at")
      .eq("user_id", context.userId)
      .eq("course_slug", data.courseSlug)
      .eq("module_id", data.moduleId)
      .eq("kind", "quiz")
      .order("attempted_at", { ascending: false });
    const priorRows = prior ?? [];
    const alreadyPassed = priorRows.some((p: any) => p.passed);
    if (!alreadyPassed && cooldownMin > 0 && priorRows[0]) {
      const lastAt = new Date(priorRows[0].attempted_at).getTime();
      const waitMs = cooldownMin * 60_000 - (Date.now() - lastAt);
      if (waitMs > 0) {
        throw new Error(`Please wait ${Math.ceil(waitMs / 60_000)} minute(s) before retaking this quiz.`);
      }
    }
    if (!alreadyPassed && maxAttempts > 0 && priorRows.length >= maxAttempts) {
      throw new Error(`Maximum ${maxAttempts} attempts reached for this quiz.`);
    }

    const { data: qs, error: qErr } = await supabaseAdmin
      .from("course_questions")
      .select("id, correct_index, points, explanation, prompt")
      .eq("course_slug", data.courseSlug)
      .eq("module_id", data.moduleId)
      .eq("kind", "quiz")
      .order("order_index");
    if (qErr || !qs?.length) throw new Error("No questions for module");

    // Resolve per-question response. Prefer id-keyed responses (randomized order safe).
    const respMap = new Map<string, number>();
    if (data.responses?.length) {
      for (const r of data.responses) respMap.set(r.questionId, r.selectedIndex);
    } else if (data.answers?.length) {
      qs.forEach((q, idx) => respMap.set(q.id, data.answers![idx] ?? -1));
    }

    let score = 0;
    let max = 0;
    const details = qs.map((q) => {
      max += q.points;
      const selected = respMap.get(q.id) ?? -1;
      const correct = selected === q.correct_index;
      if (correct) score += q.points;
      // Never reveal the full answer key; expose correctIndex only for
      // questions the student got wrong so retakes can't be gamed.
      return {
        questionId: q.id,
        selectedIndex: selected,
        correctIndex: correct ? null : q.correct_index,
        correct,
        explanation: correct ? null : (q.explanation ?? null),
      };
    });
    const pct = max ? Math.round((score / max) * 100) : 0;
    const passed = pct >= threshold;

    await sb.from("course_attempts").insert({
      user_id: context.userId,
      course_slug: data.courseSlug,
      module_id: data.moduleId,
      kind: "quiz",
      score,
      max_score: max,
      passed,
      answers: details.map((d) => d.selectedIndex),
    });

    // Update activity + time spent (column-level grants permit these for owner)
    const { data: prog } = await sb
      .from("course_progress")
      .select("passed_modules, time_spent_seconds")
      .eq("user_id", context.userId)
      .eq("course_slug", data.courseSlug)
      .maybeSingle();
    const arr: string[] = Array.isArray(prog?.passed_modules) ? (prog!.passed_modules as string[]) : [];
    const newTime = (prog?.time_spent_seconds ?? 0) + Math.min(data.elapsedSeconds ?? 0, 60 * 60);
    try {
      await sb
        .from("course_progress")
        .update({ last_activity_at: new Date().toISOString(), time_spent_seconds: newTime })
        .eq("user_id", context.userId)
        .eq("course_slug", data.courseSlug);
    } catch {
      /* protected-column trigger noise: ignore */
    }

    if (passed && !arr.includes(data.moduleId)) {
      arr.push(data.moduleId);
      await supabaseAdmin
        .from("course_progress")
        .update({ passed_modules: arr })
        .eq("user_id", context.userId)
        .eq("course_slug", data.courseSlug);
    }

    return { score, max, pct, passed, threshold, details, attemptCount: priorRows.length + 1 };
  });

const submitFinalInput = z.object({
  courseSlug: z.string().min(2).max(60),
  answers: z.array(z.number().int().min(0).max(10)).min(5).max(50).optional(),
  responses: z
    .array(
      z.object({
        questionId: z.string().uuid(),
        selectedIndex: z.number().int().min(0).max(10),
      }),
    )
    .min(5)
    .max(50)
    .optional(),
  elapsedSeconds: z.number().int().min(0).max(60 * 60 * 4).optional(),
});

export const submitFinalExam = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => submitFinalInput.parse(i))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Must have passed all module quizzes
    const { data: modules } = await sb
      .from("course_modules")
      .select("id")
      .eq("course_slug", data.courseSlug);
    const { data: prog } = await sb
      .from("course_progress")
      .select("passed_modules, status")
      .eq("user_id", context.userId)
      .eq("course_slug", data.courseSlug)
      .maybeSingle();
    if (!prog) throw new Error("Enrol first");
    const passedMods: string[] = Array.isArray(prog.passed_modules)
      ? (prog.passed_modules as string[])
      : [];
    const allModulesPassed = (modules ?? []).every((m: any) => passedMods.includes(m.id));
    if (!allModulesPassed) {
      throw new Error("Pass every module quiz before attempting the final exam");
    }

    const { data: course } = await sb
      .from("course_catalog")
      .select("pass_threshold, retake_cooldown_minutes, max_attempts_per_quiz")
      .eq("slug", data.courseSlug)
      .maybeSingle();
    const threshold = course?.pass_threshold ?? 60;
    const cooldownMin = course?.retake_cooldown_minutes ?? 0;
    const maxAttempts = course?.max_attempts_per_quiz ?? 0;

    // Retake policy
    const { data: priorFinal } = await sb
      .from("course_attempts")
      .select("id, passed, attempted_at")
      .eq("user_id", context.userId)
      .eq("course_slug", data.courseSlug)
      .eq("kind", "final")
      .order("attempted_at", { ascending: false });
    const priorRows = priorFinal ?? [];
    const alreadyPassed = priorRows.some((p: any) => p.passed);
    if (!alreadyPassed && cooldownMin > 0 && priorRows[0]) {
      const waitMs = cooldownMin * 60_000 - (Date.now() - new Date(priorRows[0].attempted_at).getTime());
      if (waitMs > 0) {
        throw new Error(`Please wait ${Math.ceil(waitMs / 60_000)} minute(s) before retaking the final exam.`);
      }
    }
    if (!alreadyPassed && maxAttempts > 0 && priorRows.length >= maxAttempts) {
      throw new Error(`Maximum ${maxAttempts} final-exam attempts reached.`);
    }

    const { data: qs } = await supabaseAdmin
      .from("course_questions")
      .select("id, correct_index, points, explanation")
      .eq("course_slug", data.courseSlug)
      .eq("kind", "final")
      .order("order_index");
    if (!qs?.length) throw new Error("No final exam configured");

    const respMap = new Map<string, number>();
    if (data.responses?.length) {
      for (const r of data.responses) respMap.set(r.questionId, r.selectedIndex);
    } else if (data.answers?.length) {
      qs.forEach((q, idx) => respMap.set(q.id, data.answers![idx] ?? -1));
    }

    let score = 0;
    let max = 0;
    const details = qs.map((q) => {
      max += q.points;
      const selected = respMap.get(q.id) ?? -1;
      const correct = selected === q.correct_index;
      if (correct) score += q.points;
      // Never leak the full answer key — only expose the correct option for
      // questions the student missed.
      return {
        questionId: q.id,
        selectedIndex: selected,
        correctIndex: correct ? null : q.correct_index,
        correct,
        explanation: correct ? null : (q.explanation ?? null),
      };
    });
    const pct = max ? Math.round((score / max) * 100) : 0;
    const passed = pct >= threshold;

    await sb.from("course_attempts").insert({
      user_id: context.userId,
      course_slug: data.courseSlug,
      module_id: null,
      kind: "final",
      score,
      max_score: max,
      passed,
      answers: details.map((d) => d.selectedIndex),
    });

    try {
      const { data: cur } = await sb
        .from("course_progress")
        .select("time_spent_seconds")
        .eq("user_id", context.userId)
        .eq("course_slug", data.courseSlug)
        .maybeSingle();
      await sb
        .from("course_progress")
        .update({
          last_activity_at: new Date().toISOString(),
          time_spent_seconds: (cur?.time_spent_seconds ?? 0) + Math.min(data.elapsedSeconds ?? 0, 60 * 60),
        })
        .eq("user_id", context.userId)
        .eq("course_slug", data.courseSlug);
    } catch { /* protected-column noise */ }

    if (passed) {
      await supabaseAdmin
        .from("course_progress")
        .update({
          status: "awaiting_review",
          final_score: pct,
        })
        .eq("user_id", context.userId)
        .eq("course_slug", data.courseSlug);
    }

    return { score, max, pct, passed, threshold, details, attemptCount: priorRows.length + 1 };
  });

// ---------- Admin ----------

async function assertAdmin(ctx: { supabase: any; userId: string }) {
  const { data, error } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (error || !data) throw new Error("Forbidden");
}

export const adminListPendingReviews = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("course_progress")
      .select(`
        id, user_id, course_slug, status, final_score, reviewed_at, reviewer_note, contract_accepted_at,
        course_catalog ( code, title ),
        profiles!course_progress_user_id_fkey ( email, full_name, student_number )
      `)
      .in("status", ["awaiting_review", "certified", "rejected"])
      .order("status")
      .order("reviewed_at", { ascending: false, nullsFirst: true })
      .limit(300);
    if (error) {
      // Fallback if FK alias unsupported
      const { data: rows } = await supabaseAdmin
        .from("course_progress")
        .select("*")
        .in("status", ["awaiting_review", "certified", "rejected"])
        .order("updated_at", { ascending: false })
        .limit(300);
      return { items: rows ?? [] };
    }
    return { items: data ?? [] };
  });

export const adminApproveCertificate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ progressId: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { canonicalPayload, signPayload, writeAuditLog } =
      await import("./certificates.server");

    const { data: prog, error: pErr } = await supabaseAdmin
      .from("course_progress")
      .select("id, user_id, course_slug, status, final_score")
      .eq("id", data.progressId)
      .maybeSingle();
    if (pErr || !prog) throw new Error("Progress not found");
    if (prog.status !== "awaiting_review") {
      throw new Error(`Cannot approve from status ${prog.status}`);
    }

    const { data: course } = await supabaseAdmin
      .from("course_catalog")
      .select("code, title")
      .eq("slug", prog.course_slug)
      .maybeSingle();
    const { data: profile } = await supabaseAdmin
      .from("profiles")
      .select("full_name, email")
      .eq("id", prog.user_id)
      .maybeSingle();
    if (!course || !profile) throw new Error("Missing course or learner profile");

    // Issue certificate via existing RPC (track 'tutoring' is the closest existing enum value)
    const { data: rows, error: iErr } = await supabaseAdmin.rpc("issue_certificate", {
      _user_id: prog.user_id,
      _track: "tutoring",
      _track_ref: course.code,
      _title: `${course.code} — ${course.title}`,
      _recipient_name: profile.full_name || profile.email || "Learner",
      _metadata: {
        academy: true,
        course_slug: prog.course_slug,
        final_score: prog.final_score,
        approved_by: context.userId,
      },
    });
    if (iErr) throw new Error(iErr.message);
    const row = Array.isArray(rows) ? rows[0] : rows;
    const certId = row.id as string;
    const code = row.verification_code as string;

    const { data: full } = await supabaseAdmin
      .from("certificates")
      .select("verification_code, recipient_name, title, track, track_ref, issued_at")
      .eq("id", certId)
      .maybeSingle();
    const canonical = canonicalPayload({
      verification_code: code,
      recipient_name: full?.recipient_name ?? profile.full_name ?? "Learner",
      title: full?.title ?? `${course.code} — ${course.title}`,
      track: full?.track ?? "tutoring",
      track_ref: full?.track_ref ?? course.code,
      issued_at: full?.issued_at ?? new Date().toISOString(),
    });
    const signature = signPayload(canonical);
    await supabaseAdmin
      .from("certificates")
      .update({
        signature,
        signature_alg: "HMAC-SHA256",
        signed_payload: canonical,
        signed_at: new Date().toISOString(),
      })
      .eq("id", certId);

    await supabaseAdmin
      .from("course_progress")
      .update({
        status: "certified",
        reviewed_by: context.userId,
        reviewed_at: new Date().toISOString(),
        certificate_id: certId,
      })
      .eq("id", prog.id);

    await writeAuditLog({
      certificate_id: certId,
      action: "issued",
      actor_id: context.userId,
      actor_role: "admin",
      signature,
      metadata: { academy: true, course_slug: prog.course_slug, progress_id: prog.id },
    });

    return { ok: true, certificateId: certId, verificationCode: code };
  });

export const adminRejectCourseSubmission = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      progressId: z.string().uuid(),
      note: z.string().trim().min(3).max(1000),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("course_progress")
      .update({
        status: "rejected",
        reviewed_by: context.userId,
        reviewed_at: new Date().toISOString(),
        reviewer_note: data.note,
      })
      .eq("id", data.progressId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
